// =====================================================
// REFER & EARN API CLIENT
//
// One thin wrapper around /api/referral and /api/admin/referrals. It owns
// nothing but transport: the BASE_URL rule the rest of the app uses, the auth
// header, and the shape each endpoint returns.
//
// Every rule about referrals — who qualifies, what counts as usage, when ₹25 is
// paid — lives on the server. Nothing here decides anything; a modified client
// can only ever ask, never award.
// =====================================================

import axios from "axios";

export const BASE_URL =
  typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

const token = () => localStorage.getItem("token");

// Created per call so it always picks up the freshest token (the same pattern
// activityApi.js already uses).
function api() {
  return axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${token()}` },
  });
}

// =====================================================
// REFERRER SIDE
// =====================================================

// STEP 2 / 9 / 14 — link, counters, wallet and every progress card in one call.
export const getDashboard = () =>
  api().get("/api/referral/dashboard").then((r) => r.data);

// =====================================================
// REFERRED USER SIDE
// =====================================================

// STEP 5 — "what do I still have to do?". Resolves to null for a user who is
// not in the programme, so the caller can simply not render the banner.
export const getProgress = () =>
  api()
    .get("/api/referral/progress")
    .then((r) => r.data.progress)
    .catch(() => null);

// CONDITION 1 — report the install signal the browser gave us. Best-effort: the
// server decides whether it counts, and a failure here is never shown to the
// user (the signal fires again on the next launch).
export const sendInstallSignal = (source) =>
  api()
    .post("/api/referral/install-signal", { source })
    .then((r) => r.data)
    .catch(() => null);

// =====================================================
// WALLET / PAYOUTS (STEP 16 / 17 / 18)
// =====================================================

export const getWallet = () => api().get("/api/referral/wallet").then((r) => r.data);

// PART 9 — `payload.otpTicket` is REQUIRED: the single-use handle returned by
// POST /api/otp/verify after the user enters the code we emailed them. The
// server spends it before it writes anything, so a save without one stores
// nothing at all.
export const saveBankDetails = (payload) =>
  api().put("/api/referral/bank", payload).then((r) => r.data);

// PART 10 — the reward withdrawal is confirmed by the SAME OTP system; only
// the purpose differs (`reward_withdraw`). The ticket is spent before the
// wallet is touched.
export const requestWithdrawal = (amount, otpTicket) =>
  api().post("/api/referral/withdraw", { amount, otpTicket }).then((r) => r.data);

// =====================================================
// ADMIN (STEP 19 → 24)
// =====================================================

export const adminReferral = {
  overview: () => api().get("/api/admin/referrals/overview").then((r) => r.data.overview),

  list: (params) =>
    api().get("/api/admin/referrals/list", { params }).then((r) => r.data),

  fraudQueue: (params) =>
    api().get("/api/admin/referrals/fraud-queue", { params }).then((r) => r.data),

  detail: (id) => api().get(`/api/admin/referrals/${id}`).then((r) => r.data.referral),

  review: (id, action, reason) =>
    api().post(`/api/admin/referrals/${id}/review`, { action, reason }).then((r) => r.data),

  payouts: (params) =>
    api().get("/api/admin/referrals/payouts/list", { params }).then((r) => r.data),

  payoutDetail: (id) =>
    api().get(`/api/admin/referrals/payouts/${id}`).then((r) => r.data.withdrawal),

  payoutAction: (id, payload) =>
    api().post(`/api/admin/referrals/payouts/${id}/action`, payload).then((r) => r.data),

  verifyBank: (id, status) =>
    api().post(`/api/admin/referrals/payouts/${id}/verify-bank`, { status }).then((r) => r.data),

  // ── PROGRAMME SETTINGS — reward, qualifying time, conditions ──
  // The server validates and stores every value; this only sends the request.
  settings: () => api().get("/api/admin/referrals/settings").then((r) => r.data),

  saveSettings: (payload) =>
    api().put("/api/admin/referrals/settings", payload).then((r) => r.data),

  addCondition: (payload) =>
    api().post("/api/admin/referrals/settings/conditions", payload).then((r) => r.data),

  updateCondition: (key, payload) =>
    api().put(`/api/admin/referrals/settings/conditions/${key}`, payload).then((r) => r.data),
};

// One condition's state, as the server's condition engine reports it.
export const CONDITION_STATUS_META = {
  passed: { label: "Passed", icon: "✅", color: "#34d399" },
  pending: { label: "Pending", icon: "⏳", color: "#fbbf24" },
  failed: { label: "Failed", icon: "❌", color: "#f87171" },
  unverified: { label: "Cannot verify", icon: "❔", color: "#94a3b8" },
};

export const conditionStatusMeta = (status) =>
  CONDITION_STATUS_META[status] || { label: status || "Unknown", icon: "•", color: "#94a3b8" };

// A referral's overall eligibility, as the admin reads it.
export const ELIGIBILITY_META = {
  eligible: { label: "Eligible", color: "#34d399" },
  pending: { label: "Pending", color: "#fbbf24" },
  ineligible: { label: "Ineligible", color: "#f87171" },
  rejected: { label: "Rejected", color: "#94a3b8" },
  paid: { label: "Approved & credited", color: "#60a5fa" },
};

// =====================================================
// SHARED FORMATTERS
//
// The dashboard, the qualification banner and the admin panel all render the
// same three things — a usage clock, a countdown and a status chip — so the
// formatting lives here once instead of three times.
// =====================================================

// 402000 → "06:42". Used for both "06:42 / 10:00" halves.
export function formatClock(ms) {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// 62280000 → "17h 18m". Falls back to minutes under an hour, and says
// "Expired" rather than showing a zero countdown.
export function formatRemaining(ms) {
  if (!ms || ms <= 0) return "Expired";
  const totalMinutes = Math.floor(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  }
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// STEP 13's status vocabulary, with the exact markers the task specifies.
export const STATUS_META = {
  pending: { label: "Pending", icon: "🟡", color: "#fbbf24" },
  in_progress: { label: "In Progress", icon: "🔵", color: "#60a5fa" },
  completed: { label: "Completed", icon: "🟢", color: "#34d399" },
  expired: { label: "Expired", icon: "🔴", color: "#f87171" },
  under_review: { label: "Under Review", icon: "⚠️", color: "#fb923c" },
  rejected: { label: "Rejected", icon: "❌", color: "#94a3b8" },
};

export const statusMeta = (status) =>
  STATUS_META[status] || { label: status || "Unknown", icon: "•", color: "#94a3b8" };

export function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
